/**
 * UnsavedChangesDialog — modal de confirmation perte de modifications.
 *
 * Pattern parité [RevokeIntakeLinkDialog 9.N.3.A] :
 *   - role="dialog" + aria-modal + aria-labelledby + aria-describedby
 *   - focus initial sur "Rester sur la page" (anti-clic-réflexe sur "Quitter")
 *   - Esc → stay (équivalent Cancel)
 *   - Backdrop click → stay
 *   - Body scroll lock pendant ouverture
 *   - Bilingue FR/EN via i18next
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  readonly open: boolean;
  /** Message custom (bilingue) à afficher dans le body. Si null, fallback générique. */
  readonly message: string | null;
  readonly onStay: () => void;
  readonly onLeave: () => void;
};

export function UnsavedChangesDialog({ open, message, onStay, onLeave }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const stayBtnRef = useRef<HTMLButtonElement | null>(null);

  // Focus initial sur "Rester" + body scroll lock pendant ouverture.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    queueMicrotask(() => stayBtnRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Esc → stay.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onStay();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onStay]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        onClick={onStay}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2
            id={titleId}
            className="text-base font-semibold text-slate-900"
          >
            {t("unsaved_changes.title")}
          </h2>
        </header>

        <div className="px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {message ?? t("unsaved_changes.body_default")}
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={onLeave}
            className="rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 touch-manipulation"
          >
            {t("unsaved_changes.leave")}
          </button>
          <button
            ref={stayBtnRef}
            type="button"
            onClick={onStay}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
          >
            {t("unsaved_changes.stay")}
          </button>
        </footer>
      </div>
    </div>
  );
}
